import { CloudUpload, AlertCircle, RotateCw, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { outboxActions, type OutboxEntry } from '@sportschool/shared-logic';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useOutboxErrorMessage } from './useOutboxErrorMessage';

interface OutboxEntryStatusProps {
  entry: OutboxEntry | undefined;
  className?: string;
  /**
   * How the "waiting to be sent" state shows: a badge, a small icon (placed over another element so nothing
   * moves while a change is sent), or not at all (when the icon is rendered elsewhere).
   */
  pendingAs?: 'badge' | 'icon' | 'none';
}

/**
 * Sync state of one queued change: a subtle "waiting to be sent" badge, or a red rejected state
 * with the translated server error and Retry / Discard actions. Renders nothing without an entry.
 */
export function OutboxEntryStatus({ entry, className, pendingAs = 'badge' }: OutboxEntryStatusProps) {
  const { t } = useTranslation();
  const getErrorMessage = useOutboxErrorMessage();

  if (!entry) return null;

  if (entry.state === 'pending') {
    if (pendingAs === 'none') return null;
    if (pendingAs === 'icon') {
      return (
        <span
          data-testid="outbox-pending-badge"
          className={cn(
            'inline-flex size-5 items-center justify-center rounded-full bg-amber-100 text-warning ring-2 ring-card dark:bg-amber-900',
            className
          )}
          role="status"
          title={t('sync.pending')}
        >
          <CloudUpload className="size-3 shrink-0 animate-pulse" aria-hidden="true" />
          <span className="sr-only">{t('sync.pending')}</span>
        </span>
      );
    }
    return (
      <span
        data-testid="outbox-pending-badge"
        className={cn(
          'inline-flex items-center gap-1 rounded-md bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-warning',
          className
        )}
        role="status"
      >
        <CloudUpload className="size-3.5 shrink-0" aria-hidden="true" />
        {t('sync.pending')}
      </span>
    );
  }

  return (
    <div
      data-testid="outbox-rejected-badge"
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-[11px] text-destructive',
        className
      )}
      role="alert"
    >
      <span className="inline-flex min-w-0 items-center gap-1 font-semibold">
        <AlertCircle className="size-3.5 shrink-0" aria-hidden="true" />
        <span className="min-w-0">
          {t('sync.rejected')}: {getErrorMessage(entry)}
        </span>
      </span>
      <span className="ml-auto flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-11 gap-1 rounded-lg px-3 text-xs"
          onClick={() => void outboxActions.retry(entry.key)}
        >
          <RotateCw className="size-3.5" aria-hidden="true" />
          {t('sync.retry')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-11 gap-1 rounded-lg px-3 text-xs"
          onClick={() => void outboxActions.discard(entry.key)}
        >
          <Trash2 className="size-3.5" aria-hidden="true" />
          {t('sync.discard')}
        </Button>
      </span>
    </div>
  );
}
